import { useForm, Controller } from 'react-hook-form'
import { useContext, useEffect } from 'react'
import { ErrorMessage } from '@hookform/error-message'

import { useAsyncError } from '../errorBoundary/useAsyncError'
import { AppContext } from '../App'
import { saveGenerationForm } from '.'
import { Loader } from '../loader'
import { ImageInput } from '../imageUploader'
import { clearErrorsAction } from '../errors'
import { updateProject } from '../project'
import { FieldUpdateIndicator } from '../fieldUpdateIndicator'
import { Editor } from '../textEditor/Editor'
import { LexicalComposer } from '@lexical/react/LexicalComposer'

const MAX_CHAR_COUNT = process.env.MAX_CHAR_COUNT || 900

const editorConfig = {
  nodes: [],
  onError: (error) => {
    throw error
  }
}

const errorMessages = {
  duration_exceeded: (data) =>
    `Audio duration (${data.mediaDurationInSeconds.toFixed(2)}s) exceeds the allowed ${data.maxMediaDurationInSeconds}s.`
}

const extractErrors = (errors, el) => {
  return errors
    .filter((e) => e.path.includes(el))
    .map((e) => {
      const code = e.errorCode
      const formatter = errorMessages[code]
      return formatter ? formatter(e.data) : e.message
    })
}

const GenerationForm = () => {
  const { state, dispatch } = useContext(AppContext)
  const throwAsyncError = useAsyncError()
  const {
    reset,
    register,
    handleSubmit,
    control,
    trigger,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    reset({
      speaking_rate: state.generationForm.speakingRate,
      text: state.generationForm.text

    })
  }, [state.generationForm.speakingRate, state.generationForm.text, state.currentProject.projectId])

  const onSubmit = (data) => {
    data.project_id = state.currentProject.projectId
    data.image_name = `${state.currentProject.projectId}.${state.currentProject.imageExtension}`

    saveGenerationForm(dispatch, data, throwAsyncError)
    dispatch(clearErrorsAction())
  }

  const handleFieldsChange = async (field, value) => {
    const updatedData = { [field]: value }
    const existingValue = state.editor.updatedField[field]

    const isValid = await trigger(field)
    if (!isValid) {
      dispatch(clearErrorsAction())
      return
    }

    if (existingValue === value) {
      return
    }

    await updateProject(dispatch, state.currentProject.projectId, updatedData, throwAsyncError)
  }

  return (
    <form
      className='rounded pt-6 pb-8 w-full'
      onSubmit={handleSubmit(onSubmit)}
    >
      <label
        className='flex block text-gray-700 text-sm font-bold mt-2 items-center h-[24px]'
        htmlFor='image_name'
      >
        Image
      </label>

      <Controller
        control={control}
        rules={state.currentProject.imageHash
          ? {}
          : {
              required: 'An image must be selected'
            }}
        name='image_name'
        render={({ field }) => <ImageInput {...field} />}
      />

      <div className='min-h-[24px] mt-2'>
        <span className='text-red-700 font-bold w-full flex '>
          <ErrorMessage errors={errors} name='image_name' />
        </span>

        {state.currentProject.details && state.currentProject.details.length > 0 && (
          <span className='text-red-700 font-bold w-full flex '>
            {extractErrors(state.currentProject.details, 'image_name')}
          </span>
        )}
      </div>

      <label
        className='flex block text-gray-700 text-sm font-bold mt-2 items-center h-[24px]'
        htmlFor='speaking_rate'
      >
        Speaking Rate
        {'speaking_rate' in state.editor.updatedField && <FieldUpdateIndicator fieldName='speaking_rate' />}
      </label>

      <input
        className='input input-bordered w-full shadow py-2 px-3  decoration-1 placeholder-gray-600'
        placeholder='0.25 - 4.0'
        defaultValue={state.generationForm.speakingRate}
        {...register('speaking_rate', {
          required: 'This field is required',
          validate: {
            isNumber: (value) => !isNaN(value) || 'This input must be a number',
            min: (value) => value >= 0.25 || 'This input must be greater than or equal to 0.25',
            max: (value) => value <= 4.0 || 'This input must be less than or equal to 4.0'
          },
          onBlur: (e) => {
            handleFieldsChange('speaking_rate', e.target.value)
          }
        })}
        id='speaking_rate'
      />

      <div className='min-h-[24px] mt-2'>
        <span className='text-red-700 font-bold w-full flex '>
          <ErrorMessage errors={errors} name='speaking_rate' />
        </span>

        {state.currentProject.details && state.currentProject.details.length > 0 && (
          <span className='text-red-700 font-bold w-full flex '>
            {extractErrors(state.currentProject.details, 'speaking_rate')}
          </span>
        )}
      </div>

      <label
        className='flex block text-gray-700 text-sm font-bold mt-2 items-center h-[24px]'
        htmlFor='ssml_text'
      >
        Text
        {'text' in state.editor.updatedField && <FieldUpdateIndicator fieldName='text' />}
      </label>

      <Controller
        control={control}
        name='text'
        rules={{
          required: 'This field is required',
          maxLength: {
            value: MAX_CHAR_COUNT,
            message: `Max length is ${MAX_CHAR_COUNT} characters`
          }
        }}
        render={({ field }) => (
          <LexicalComposer initialConfig={editorConfig}>
            <Editor
              onBlur={(e) => {
                if (state.editor.drilldownLevel === null && state.editor.isUserActiveInEditor) {
                  field.onChange(e.target.value)
                  handleFieldsChange('text', e.target.value)
                }
              }}
            />
          </LexicalComposer>
        )}
      />

      <div className='min-h-[24px] mt-2'>
        <span className='text-red-700 font-bold w-full flex '>
          <ErrorMessage errors={errors} name='text' />
        </span>

        {state.currentProject.details && state.currentProject.details.length > 0 && (
          <span className='text-red-700 font-bold w-full flex '>
            {extractErrors(state.currentProject.details, 'text')}
            {extractErrors(state.currentProject.details, 'voices')}
          </span>
        )}
      </div>

      <div className='flex justify-end'>
        {state.loading
          ? (
            <Loader />
            )
          : (
            <button
              className='items-center mt-2 py-2 px-4 rounded focus:outline-none focus:shadow-outline bg-blue-500 hover:bg-blue-700 text-white'
              type='submit'
            >
              {' '}
              Generate
            </button>
            )}
      </div>
    </form>
  )
}

export { GenerationForm }
